import {
  buildCarHireCrossSellUrl,
  buildHotelCrossSellUrl,
} from '@skyscanner-internal/global-components/header/helpers';
import { SCREEN_NAMES } from '@web-platform/shared-universal-constants/src/screenName';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import {
  getDestination,
  getInboundDate,
  getOutboundDate,
} from '@web-platform/shared-universal-redux/src/selectors/search';
import { translate } from '@web-platform/shared-universal-translations';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { addDays } from 'date-fns/addDays';
// eslint-disable-next-line @skyscanner-internal/skyscanner-dates/no-date-fns-format
import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
import { useSelector } from 'react-redux';

import type { ScreenName } from '@web-platform/shared-universal-constants/src/screenName';

export const DEFAULT_CAR_HIRE_ADULT_AGE = 30;

export const TABS = {
  FLIGHTS: 'FLIGHTS',
  HOTELS: 'HOTELS',
  CARHIRE: 'CARHIRE',
  PACKAGES: 'PACKAGES',
  CRUISES: 'CRUISES',
} as const;

export type TabTypes = keyof typeof TABS;

export type TabDataType = {
  tabType: TabTypes;
  tabText: string;
  url?: string;
  openInNewTab?: boolean;
};

const calculateReturnDate = (outboundDate: string, inboundDate: string, daysToAdd: number) => {
  const outbound = parseISO(outboundDate);
  const inbound = inboundDate && parseISO(inboundDate);

  if (!isValid(outbound) || (inboundDate && !isValid(inbound))) {
    return '';
  }

  if (inbound && outbound.getTime() !== inbound.getTime()) {
    return inboundDate;
  }

  return format(addDays(outbound, daysToAdd), 'yyyy-MM-dd');
};

const buildHotelsUrl = (
  screenName: ScreenName,
  entityId?: string,
  checkin?: string,
  checkout?: string,
) => {
  if (!entityId || !checkin || !checkout || screenName !== SCREEN_NAMES.COMBINED_RESULTS) {
    const enableLandingOnDayview = getConfigServiceValue(
      CONFIG_SERVICE_KEYS.EXPERIMENT.ENABLE_HOTELS_TAB_DAYVIEW_LANDING,
    );

    return enableLandingOnDayview ? `/${translate('hotels')}/search` : `/${translate('hotels')}`;
  }

  return buildHotelCrossSellUrl({
    entityId,
    checkin,
    checkout,
  });
};

const buildCarHireUrl = (
  screenName: ScreenName,
  pickUpPlace?: string,
  pickUpDatetime?: string,
  dropOffPlace?: string,
  dropOffDatetime?: string,
  driverAge?: number,
) => {
  if (
    !pickUpPlace ||
    !pickUpDatetime ||
    !driverAge ||
    !dropOffPlace ||
    !dropOffDatetime ||
    screenName !== SCREEN_NAMES.COMBINED_RESULTS
  ) {
    return `/${translate('car_hire')}`;
  }

  return buildCarHireCrossSellUrl({
    pickUpDatetime,
    pickUpPlace,
    dropOffDatetime,
    dropOffPlace,
    driverAge,
  });
};

export const useGlobalTabsData = (screenName: ScreenName) => {
  const destination = useSelector(getDestination);
  const outboundDate = useSelector(getOutboundDate);
  const inboundDate = useSelector(getInboundDate);

  const hotelsUrl = buildHotelsUrl(
    screenName,
    destination?.geoContainerId,
    outboundDate,
    outboundDate && calculateReturnDate(outboundDate, inboundDate, 1),
  );

  const carHireUrl = buildCarHireUrl(
    screenName,
    destination?.geoContainerId ? destination.geoContainerId : '',
    outboundDate,
    destination?.geoContainerId ? destination.geoContainerId : '',
    outboundDate && calculateReturnDate(outboundDate, inboundDate, 7),
    DEFAULT_CAR_HIRE_ADULT_AGE,
  );

  const isPackageExperimentEnabled = getFeatureDecision(
    FEATURE_DECISION_KEYS.PACKAGES_HEADER_TAB_ENABLED,
  );

  const isCruisesTabEnabled = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_CRUISES_TAB);
  const cruisesUrl = getConfigServiceValue(CONFIG_SERVICE_KEYS.EXPERIMENT.CRUISES_TAB_LINK);

  const isStaysEnabled = getFeatureDecision(FEATURE_DECISION_KEYS.STAYS_COPY_HEADER_TAB_ENABLED);

  const tabs: TabDataType[] = [
    {
      tabType: TABS.FLIGHTS,
      tabText: translate('homepageTab_flights'),
    },
    {
      tabType: TABS.HOTELS,
      tabText: isStaysEnabled ? translate('homepageTab_stays') : translate('homepageTab_hotels'),
      url: hotelsUrl,
    },
    {
      tabType: TABS.CARHIRE,
      tabText: translate('ktxtHeader_cars_label'),
      url: carHireUrl,
    },
  ];

  if (isPackageExperimentEnabled) {
    tabs.push({
      tabType: TABS.PACKAGES,
      tabText: translate('ktxtHeader_packages_label'),
      url: '/destinations/package-holidays',
    });
  }

  if (isCruisesTabEnabled) {
    tabs.push({
      tabType: TABS.CRUISES,
      tabText: translate('ktxtHeader_cruises_label'),
      url: cruisesUrl,
      openInNewTab: true,
    });
  }

  return tabs;
};
